import { useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { checkIsActive, KTSVG } from '@helpers/index';
import { useLayout } from '@layout/core';
import { MENU_DISCOUNT_BADGE_LABEL } from '@constants/navi';
import { getHasUpsellDiscountSelector } from '@modules/shop/core/selectors';

type AsideMenuItemProps = {
  to: string;
  title: string;
  icon?: string;
  iconActive?: string;
  sub?: boolean;
  type?: 'internal' | 'external';
  /** Mirrors `MenuItem.hasDiscountIcon`; renders the discount badge by the title. */
  hasDiscountIcon?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  cssClasses?: string[];
};

const AsideMenuItem = ({
  to,
  title,
  icon,
  iconActive,
  sub,
  type = 'internal',
  hasDiscountIcon,
  onClick,
  cssClasses,
}: AsideMenuItemProps) => {
  const { pathname } = useLocation();
  const isActive = checkIsActive(pathname, to);
  const hasUpsellDiscount = useSelector(getHasUpsellDiscountSelector);
  const { config } = useLayout();
  const { aside } = config;
  const [isHovered, setIsHovered] = useState(false);

  const currentIcon = iconActive && (isActive || isHovered) ? iconActive : icon;

  // Three conditions, and the badge needs all of them:
  //  - the menu entry opts in via `hasDiscountIcon`,
  //  - a discount is actually live in the upsell feed, so the badge never
  //    promises a sale that has already ended, and
  //  - this is not the current page: the badge advertises a destination, so it has
  //    nothing left to say to someone already standing on it. Keyed on `isActive`
  //    rather than a literal '/mitbestellen' so any future flagged entry behaves
  //    the same way.
  const showDiscountBadge = hasDiscountIcon && hasUpsellDiscount && !isActive;

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  return (
    <div className={clsx('menu-item', cssClasses?.join(' '), { 'menu-item-sub': sub })}>
      {type === 'internal' ? (
        <Link
          className={clsx('menu-link without-sub', { 'fill-black': !isActive, active: isActive })}
          to={to}
          onClick={onClick}
          onMouseOver={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>
          {currentIcon && aside.menuIcon === 'svg' && (
            <span className="menu-icon">
              {icon && (
                <KTSVG
                  path={icon}
                  className={clsx('svg-icon-2', { inactive: !isActive && !isHovered })}
                />
              )}
              {iconActive && (
                <KTSVG
                  path={iconActive}
                  className={clsx('svg-icon-2', { active: isActive || isHovered })}
                />
              )}
            </span>
          )}
          <span className="menu-title">
            {title}
            {showDiscountBadge && (
              <span className="menu-link-badge">{MENU_DISCOUNT_BADGE_LABEL}</span>
            )}
          </span>
        </Link>
      ) : type === 'external' ? (
        <a
          className={clsx('menu-link without-sub font-bold text-[1.4rem] md:text-[2rem]', {
            'fill-black': !isActive,
            active: isActive,
          })}
          href={to}
          target="_blank"
          rel="noopener noreferrer">
          {icon && aside.menuIcon === 'svg' && (
            <span className="menu-icon">
              <KTSVG path={icon} className="svg-icon-2" />
            </span>
          )}
          <span className="menu-title">
            {title}
            {showDiscountBadge && (
              <span className="menu-link-badge">{MENU_DISCOUNT_BADGE_LABEL}</span>
            )}
          </span>
        </a>
      ) : null}
    </div>
  );
};

export { AsideMenuItem };
